import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { CategoryService } from 'app/services/category/category.service';
import { CategoryDto } from 'app/models/category-response';
import { environment } from 'app/environments/environment';
import { ImageUrlPipe } from 'app/modules/admin/pipe/image-url.pipe';
import { GalleryPickerComponent } from 'app/shared/gallery-picker/gallery-picker.component';
import { ImageFallbackDirective } from 'app/modules/admin/directives/ImageFallbackDirective';
import { ImageItem } from 'app/services/gallery/gallery.service';

@Component({
  selector: 'app-edit-category',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, ImageUrlPipe, GalleryPickerComponent, ImageFallbackDirective],
  templateUrl: './edit-category.component.html',
  styleUrls: ['./edit-category.component.scss']
})
export class EditCategoryComponent implements OnInit {
  categoryForm: FormGroup;
  
  // الفئات الأب (سيتم تحميلها كلها دفعة واحدة)
  parentCategories: any[] = [];
  
  // متغيرات التصفح (غير مستخدمة فعلياً بعد التعديل)
  currentPage: number = 1;
  itemsPerPage: number = 1000;
  totalItems: number = 0;
  totalPages: number = 0;
  
  isLoading: boolean = false;
  isSubmitting: boolean = false;
  
  selectedFileName: string = '';
  imagePreview: string | null = null;
  selectedFile: File | null = null;
  currentCategory: any = null;
  categorySlug: string = '';
  
  isGalleryModalOpen = false;
  selectedPhotos: ImageItem[] = [];
  
  lang: string = 'eg';

  constructor(
    private fb: FormBuilder,
    private categoryService: CategoryService,
    private router: Router,
    private route: ActivatedRoute
  ) {
    this.categoryForm = this.createForm();
  }

  ngOnInit() {
    this.categorySlug = this.route.snapshot.paramMap.get('slug') || '';
    if (this.categorySlug) {
      this.loadCategoryData();
    } else {
      alert('Category slug not found');
      this.router.navigateByUrl('/admin/categories');
    }
  }

  createForm(): FormGroup {
    return this.fb.group({
      parentId: [0, [Validators.required, Validators.min(0)]],
      level: [0, [Validators.required, Validators.min(0)]],
      code: ['', [Validators.required, Validators.minLength(1)]],
      arName: ['', [Validators.required, Validators.minLength(2)]],
      enName: ['', [Validators.required, Validators.minLength(2)]],
      icon: [null],
      featured: [false],
      top: [false],
      slug: [
        '',
        [
          Validators.required,
          Validators.pattern(/^[a-zA-Z0-9]+$/)
        ]
      ],
      status: [true]
    });
  }

  loadCategoryData() {
    this.isLoading = true;
    this.categoryService.getCategoryBySlug(this.categorySlug).subscribe({
      next: (response) => {
        this.currentCategory = response;
        this.populateForm(this.currentCategory);
        this.loadAllParentCategories(); // 🔥 تحميل جميع الفئات الأب
        this.isLoading = false;
      },
      error: (error) => {
        console.error('Error loading category data:', error);
        this.isLoading = false;
        alert('Failed to load category data');
        this.router.navigateByUrl('/admin/categories');
      }
    });
  }

  // 🔄 تحميل جميع الفئات الأب عبر تكرار طلب الصفحات حتى آخر صفحة
  loadAllParentCategories() {
    this.isLoading = true;
    const allCategories: any[] = [];

    const fetchPage = (page: number) => {
      this.categoryService.getSubCategories('', page).subscribe({
        next: (response) => {
          let data = response.data || [];
          // استبعاد التصنيف الحالي من القائمة
          if (this.currentCategory && this.currentCategory.id) {
            data = data.filter((cat: any) => cat.id !== this.currentCategory.id);
          }
          allCategories.push(...data);

          const currentPage = response.meta?.currentPage || page;
          const lastPage = response.meta?.lastPage || response.meta?.to || currentPage;

          if (currentPage < lastPage) {
            // نجلب الصفحة التالية
            fetchPage(currentPage + 1);
          } else {
            // جميع الصفحات انتهت
            this.parentCategories = allCategories;
            this.totalItems = allCategories.length;
            this.totalPages = 1; // لأننا نعرض الكل دفعة واحدة
            this.currentPage = 1;
            this.isLoading = false;
          }
        },
        error: (err) => {
          console.error('❌ Error loading parent categories:', err);
          // نحتفظ بأي بيانات تم تحميلها بالفعل
          this.parentCategories = allCategories;
          this.totalItems = allCategories.length;
          this.totalPages = 1;
          this.isLoading = false;
        }
      });
    };

    fetchPage(1); // نبدأ من الصفحة الأولى
  }

  // دوال التصفح (غير مستخدمة بعد التعديل، لكن يمكن الإبقاء عليها)
  goToPage(page: number) {}
  previousPage() {}
  nextPage() {}
  getPageNumbers(): number[] { return [1]; }
  getStartIndex(): number { return 1; }
  getEndIndex(): number { return this.totalItems; }
  onItemsPerPageChange(event: any) {}

  // ========== دوال النموذج والصور (بدون تغيير) ==========
  populateForm(category: CategoryDto) {
    this.categoryForm.patchValue({
      parentId: category.parent_id || 0,
      level: category.level || 0,
      code: category.code || '',
      arName: category.ar_name,
      enName: category.en_name || category.name || '',
      featured: category.featured || false,
      top: category.top || false,
      slug: category.slug || '',
      status: category.status !== undefined ? category.status : true
    });

    if (category.icon) {
      this.imagePreview = category.icon;
      this.selectedFileName = 'Current icon';
    }
  }

  onFileSelected(event: any) {
    const file = event.target.files[0];
    if (file) {
      const validTypes = ['image/jpeg', 'image/png', 'image/svg+xml', 'image/webp'];
      if (!validTypes.includes(file.type)) {
        alert('Please select a valid image file (JPG, PNG, SVG, WebP)');
        return;
      }
      if (file.size > 2 * 1024 * 1024) {
        alert('File size must be less than 2MB');
        return;
      }
      this.selectedFile = file;
      this.selectedFileName = file.name;
      this.categoryForm.patchValue({ icon: file });
      const reader = new FileReader();
      reader.onload = () => {
        this.imagePreview = reader.result as string;
      };
      reader.readAsDataURL(file);
    }
  }

  removeImage() {
    this.selectedFile = null;
    this.selectedFileName = '';
    this.imagePreview = null;
    this.categoryForm.patchValue({ icon: '' });
  }

  generateSlug() {
    const enName = this.categoryForm.get('enName')?.value;
    if (enName) {
      const slug = enName
        .toLowerCase()
        .trim()
        .replace(/[^\w\s-]/g, '')
        .replace(/[\s_-]+/g, '-')
        .replace(/^-+|-+$/g, '');
      this.categoryForm.patchValue({ slug });
    }
  }

  async onSubmit() {
    if (this.categoryForm.invalid) {
      this.markAllFieldsAsTouched();
      return;
    }
    this.isSubmitting = true;
    try {
      const formData = this.categoryForm.value;
      this.categoryService.editCategory(this.currentCategory.slug, formData).subscribe({
        next: (response) => {
          this.isSubmitting = false;
          alert('✅ Category updated successfully!');
          this.router.navigateByUrl('/admin/categories');
        },
        error: (error) => {
          this.isSubmitting = false;
          console.error('❌ Error updating category:', error);
          alert('⚠️ Failed to update category. Please try again.');
        }
      });
    } catch (error) {
      this.isSubmitting = false;
      console.error('❌ Error preparing form data:', error);
      alert('⚠️ Error preparing form data. Please check your inputs.');
    }
  }

  markAllFieldsAsTouched() {
    Object.keys(this.categoryForm.controls).forEach(key => {
      this.categoryForm.get(key)?.markAsTouched();
    });
  }

  showError(controlName: string): boolean {
    const control = this.categoryForm.get(controlName);
    return !!(control && control.invalid && (control.dirty || control.touched));
  }

  getError(controlName: string): string {
    const control = this.categoryForm.get(controlName);
    if (control?.errors) {
      if (control.errors['required']) return 'This field is required';
      if (control.errors['minlength']) return `Minimum length is ${control.errors['minlength'].requiredLength}`;
      if (control.errors['min']) return `Minimum value is ${control.errors['min'].min}`;
      if (control.errors['max']) return `Maximum value is ${control.errors['max'].max}`;
    }
    return 'Invalid value';
  }

  getCategoryName(category: any): string {
    return category?.name || category?.enName || category?.arName || 'Unnamed Category';
  }

  goBack() {
    this.router.navigateByUrl('/admin/categories');
  }

  // ========== معرض الصور ==========
  openGalleryModal(): void {
    this.isGalleryModalOpen = true;
  }

  closeGalleryModal(): void {
    this.isGalleryModalOpen = false;
  }

  onGalleryImagesChange(images: ImageItem[]): void {
    this.selectedPhotos = images || [];
    const firstImage = this.selectedPhotos[0];
    if (!firstImage) return;
    this.selectedFile = null;
    this.selectedFileName = firstImage.name || 'Gallery icon';
    this.imagePreview = firstImage.url;
    this.categoryForm.patchValue({
      icon: Number(firstImage.id)
    });
    this.closeGalleryModal();
  }
}